// src/profile/ProfileSkins.jsx
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import './tabs.css';

// === API base (как в квестах) ===
const API_ORIGIN = process.env.REACT_APP_API_URL;
const API_HOST   = API_ORIGIN;

// приведение пути к абсолютному
const toAbs = (p) => {
  if (!p) return null;
  if (/^https?:\/\//i.test(p)) return p;
  const rel = String(p).replace(/^\.\//, '/').replace(/^\./, '/');
  return `${API_HOST}${rel.startsWith('/') ? rel : `/${rel}`}`;
};

function groupSkins(skins = []) {
  const map = new Map();
  for (const s of skins) {
    const key = s.skin_type || 'common';
    const q = Number(s.quantity) || 0;
    map.set(key, (map.get(key) || 0) + q);
  }
  return [...map.entries()]
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([skin_type, qty]) => ({ skin_type, qty }));
}

// иконки скинов по типу (как в квестах)
const SKIN_ICONS_BY_TYPE = {
  wooden_dice: '/uploads/quests/wooden_dice.png',
};

// человекочитаемые названия
const SKIN_LABEL_KEYS_BY_TYPE = {
  wooden_dice: 'profile.skins.names.woodenDice',
};

function getSkinMeta(type, translate) {
  const key = type || 'skin';
  const translationKey = SKIN_LABEL_KEYS_BY_TYPE[key];
  const name = translationKey
    ? translate(translationKey)
    : translate('profile.skins.genericName', { type: key });
  const imageRel = SKIN_ICONS_BY_TYPE[key] || '/uploads/quests/skin.png';
  return {
    name,
    image: toAbs(imageRel),
  };
}

const styles = {
  // ВНЕШНЯЯ ОБЁРТКА — именно она убирает белый фон
  root: {
    width: '100%',
    minHeight: '100vh',           // чтобы до низа экрана было тёмно
    backgroundColor: '#111827',   // тот же фон, что и остальной сайт
    boxSizing: 'border-box',
    padding: '0 0 32px 0',
  },
  pageWrap: {
    maxWidth: 900,
    borderRadius: 16,
    background: '#0b1220',
    border: '1px solid #111827',
    padding: 20,
    color: '#e5e7eb',
    boxShadow: '0 18px 45px rgba(0,0,0,0.65)',
  },
  headerRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  title: {
    fontSize: 20,
    fontWeight: 700,
  },
  badge: {
    padding: '4px 10px',
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    background: 'rgba(251,191,36,0.1)',
    color: '#fbbf24',
    border: '1px solid rgba(251,191,36,0.4)',
    whiteSpace: 'nowrap',
  },
  subtitle: {
    fontSize: 13,
    color: '#9ca3af',
    lineHeight: 1.5,
    marginBottom: 16,
  },
  bulletList: {
    fontSize: 13,
    color: '#e5e7eb',
    marginBottom: 18,
  },
  bulletRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 8,
    marginBottom: 4,
  },
  bulletDot: {
    width: 16,
    height: 16,
    borderRadius: '50%',
    background: 'linear-gradient(135deg,#fbbf24,#f97316)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 11,
    color: '#111827',
    flexShrink: 0,
  },
  skinsTitle: {
    fontSize: 14,
    fontWeight: 600,
    marginBottom: 8,
  },
  skinsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill,minmax(260px,1fr))',
    gap: 12,
  },
  skinCard: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    padding: 10,
    borderRadius: 12,
    background: 'radial-gradient(circle at top,#111827,#020617)',
    border: '1px solid #1f2937',
  },
  skinLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  skinThumbWrap: {
    width: 52,
    height: 52,
    borderRadius: 12,
    overflow: 'hidden',
    border: '1px solid rgba(249, 250, 251, 0.08)',
    background:
      'repeating-linear-gradient(135deg, #111827, #111827 8px, #020617 8px, #020617 16px)',
    flexShrink: 0,
  },
  skinName: {
    fontSize: 14,
    fontWeight: 600,
  },
  skinType: {
    fontSize: 11,
    color: '#6b7280',
  },
  skinRight: {
    textAlign: 'right',
  },
  skinQty: {
    fontSize: 14,
    fontWeight: 700,
  },
  skinTag: {
    marginTop: 2,
    fontSize: 11,
    color: '#9ca3af',
  },
  footerNote: {
    marginTop: 14,
    fontSize: 12,
    color: '#9ca3af',
  },
  emptyText: {
    fontSize: 13,
    color: '#9ca3af',
  },
};

export default function ProfileSkins({ skins }) {
  const { t: translate } = useTranslation();
  const grouped = useMemo(() => groupSkins(skins), [skins]);

  return (
    <div style={styles.root}>
      <div style={styles.pageWrap}>
        {/* Заголовок блока */}
        <div style={styles.headerRow}>
          <div style={styles.title}>{translate('profile.skins.title')}</div>
          <div style={styles.badge}>{translate('profile.skins.badge')}</div>
        </div>

        {/* Описание, зачем игроку скины */}
        <div style={styles.subtitle}>
          {translate('profile.skins.subtitle')}
        </div>

        <div style={styles.bulletList}>
          <div style={styles.bulletRow}>
            <div style={styles.bulletDot}>₮</div>
            <div>
              <b>{translate('profile.skins.bullets.paymentTitle')}</b>{' '}
              {translate('profile.skins.bullets.paymentText')}
            </div>
          </div>
          <div style={styles.bulletRow}>
            <div style={styles.bulletDot}>⇄</div>
            <div>
              <b>{translate('profile.skins.bullets.marketTitle')}</b>{' '}
              {translate('profile.skins.bullets.marketText')}
            </div>
          </div>
        </div>

        <div style={styles.skinsTitle}>{translate('profile.skins.yours')}</div>

        {!grouped.length ? (
          <p style={styles.emptyText}>
            {translate('profile.skins.empty')}
          </p>
        ) : (
          <div style={styles.skinsGrid}>
            {grouped.map(({ skin_type, qty }) => {
              const meta = getSkinMeta(skin_type, translate);
              return (
                <div key={skin_type} style={styles.skinCard}>
                  <div style={styles.skinLeft}>
                    <div style={styles.skinThumbWrap}>
                      <img
                        src={meta.image}
                        alt={meta.name}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          display: 'block',
                        }}
                      />
                    </div>
                    <div>
                      <div style={styles.skinName}>{meta.name}</div>
                      <div style={styles.skinType}>ID: {skin_type}</div>
                    </div>
                  </div>
                  <div style={styles.skinRight}>
                    <div style={styles.skinQty}>{translate('profile.common.quantity', { count: qty })}</div>
                    <div style={styles.skinTag}>
                      {translate('profile.skins.useForFees')}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div style={styles.footerNote}>
          {translate('profile.skins.footer')}
        </div>
      </div>
    </div>
  );
}






// // src/profile/ProfileSkins.jsx
// import React from 'react';
// import './tabs.css';
// function groupSkins(skins=[]) {
//   const map = new Map();
//   for (const s of skins) {
//     const key = s.skin_type || 'common';
//     const q = Number(s.quantity) || 0;
//     map.set(key, (map.get(key) || 0) + q);
//   }
//   return [...map.entries()]
//     .sort((a,b)=>a[0].localeCompare(b[0]))
//     .map(([skin_type, qty]) => ({ skin_type, qty }));
// }

// export default function ProfileSkins({ skins }) {
//   const grouped = groupSkins(skins);
//   if (!grouped.length) return <p style={{color:'#9aa3b2'}}>Скинов нет.</p>;

//   return (
//     <div className="assets-list">
//       {grouped.map(({skin_type, qty}) => (
//         <div key={skin_type} className="asset-row">
//           <div className="asset-title">Скин «{skin_type}»</div>
//           <div className="asset-qty">{qty} шт.</div>
//         </div>
//       ))}
//     </div>
//   );
// }
